import { useCallback, useEffect, useMemo, useState } from 'react';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import { MY_UPLOAD_SETTINGS, UPDATE_MY_UPLOAD_SETTINGS } from '@parsina/framework/clinical/src/screens/PatientDetail/web/queries';

// «فایل‌ها» — the doctor's own upload settings, inside the «صفحه ویزیت» tab.
// docs/DICOM_COMPRESSION_LIMITS_AND_DOCUMENT_OCR_DESIGN.md §7.5, §10.5, §23.3 A8, §24.3, §24.8.
//
// Every setting is TRI-STATE: «پیش‌فرض» (null — inherit from the clinic, then Parsina),
// «فعال», «غیرفعال». Unlike the visit toggles these are a FORM with a save bar: the size
// is a number the doctor types, and a limit half-typed is not a limit anyone asked for.
// Only the fields that changed are sent, named in the `mask`, so saving the size never
// rewrites a DICOM choice the doctor did not touch — and «پیش‌فرض» is sent as null WITH
// its field in the mask, which is how a stored value is cleared.
//
// Pure helpers are exported for the tests and so both surfaces read one meaning.

const MiB = 1024 * 1024;

export const TRI_STATES = Object.freeze(['inherit', 'on', 'off']);

const tri = (value) => (value == null ? 'inherit' : value ? 'on' : 'off');
const fromTri = (state) => (state === 'inherit' ? null : state === 'on');

export function uploadSettingsForm(settings) {
  const bytes = Number(settings?.maxFileBytes);
  return {
    sizeLimit: tri(settings?.sizeLimitEnabled),
    maxMb: Number.isFinite(bytes) && bytes > 0 ? String(Math.round((bytes / MiB) * 10) / 10) : '',
    dicom: tri(settings?.dicomEnabled),
  };
}

export const isUploadSettingsDirty = (form, saved) => Object.keys(saved).some((k) => String(form[k] ?? '') !== String(saved[k] ?? ''));

/** The ceiling a doctor's own limit may not exceed, in MB. */
export const ceilingMb = (ceilings) => Math.floor((Number(ceilings?.documentBytes) || 14 * MiB) / MiB);

/** Field errors as i18n keys. The size is required only while the limit is on. */
export function validateUploadSettings(form, ceilings) {
  const errors = {};
  if (form.sizeLimit === 'on') {
    const mb = Number(String(form.maxMb).replace(',', '.'));
    if (!String(form.maxMb).trim() || !Number.isFinite(mb) || mb <= 0) errors.maxMb = 'settings.files.errors.sizeRequired';
    else if (mb > ceilingMb(ceilings)) errors.maxMb = 'settings.files.errors.aboveCeiling';
  }
  return errors;
}

/** `{ input, mask }` for updateMyUploadSettings — changed fields only. */
export function uploadSettingsPayload(form, saved) {
  const input = {};
  const mask = [];
  if (form.sizeLimit !== saved.sizeLimit) {
    input.sizeLimitEnabled = fromTri(form.sizeLimit);
    mask.push('sizeLimitEnabled');
  }
  // The size means something only while the limit is on; turning the limit to
  // «پیش‌فرض» clears the doctor's own size too, so no stale number waits to resurface.
  const sizeTouched = form.maxMb !== saved.maxMb;
  if (form.sizeLimit === 'on' && (sizeTouched || form.sizeLimit !== saved.sizeLimit)) {
    input.maxFileBytes = Math.round(Number(String(form.maxMb).replace(',', '.')) * MiB);
    mask.push('maxFileBytes');
  } else if (form.sizeLimit === 'inherit' && form.sizeLimit !== saved.sizeLimit && saved.maxMb) {
    input.maxFileBytes = null;
    mask.push('maxFileBytes');
  }
  if (form.dicom !== saved.dicom) {
    input.dicomEnabled = fromTri(form.dicom);
    mask.push('dicomEnabled');
  }
  return { input, mask };
}

export function useUploadSettings({ enabled = true } = {}) {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [form, setForm] = useState(() => uploadSettingsForm(null));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setStatus('loading');
    setError(null);
    bffQuery(MY_UPLOAD_SETTINGS, {})
      .then((d) => {
        if (cancelled) return;
        const s = d?.myUploadSettings || null;
        setSettings(s);
        setForm(uploadSettingsForm(s));
        setStatus('content');
      })
      .catch((e) => { if (!cancelled) { setError(e); setStatus('error'); } });
    return () => { cancelled = true; };
  }, [enabled, attempt]);

  const saved = useMemo(() => uploadSettingsForm(settings), [settings]);
  const dirty = status === 'content' && isUploadSettingsDirty(form, saved);

  const setField = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    setSaveError(null);
  }, []);

  const reset = useCallback(() => { setForm(saved); setErrors({}); setSaveError(null); }, [saved]);

  const save = useCallback(async () => {
    const next = validateUploadSettings(form, settings?.ceilings);
    if (Object.keys(next).length) { setErrors(next); return { ok: false }; }
    const { input, mask } = uploadSettingsPayload(form, saved);
    if (!mask.length) return { ok: true, noop: true };
    setSaving(true);
    setSaveError(null);
    try {
      const d = await bffMutation(UPDATE_MY_UPLOAD_SETTINGS, { input, mask });
      const s = d?.updateMyUploadSettings || null;
      setSettings(s);
      setForm(uploadSettingsForm(s));
      return { ok: true };
    } catch (e) {
      setSaveError(e);
      return { ok: false, error: e };
    } finally {
      setSaving(false);
    }
  }, [form, saved, settings]);

  return {
    settings, status, error, retry: () => setAttempt((n) => n + 1),
    form, errors, setField, dirty, reset, save, saving, saveError,
  };
}

export default useUploadSettings;
